import { TraitIdentifier } from "@dimensional-engineering/dimensional-models";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Roboto_Mono } from "next/font/google";

import styles from "./SingleTraitDeltaView.module.css";

import { useStaggeredOpacity } from "@/helpers/useStaggeredOpacity";
import { getElementFromTraitIdentifier } from "@/components/authenticatedProfile/tabs/AuthCompareTab/utils";
import { ExtendedScale } from "@/models/sharedModels";
import Element from "@/components/traits/element/Element/Element";
import { getUserScaleScore } from "@/components/traits/utils";
import { getPreviousPercentScoreFromScoreHistory } from "@/components/assessments-new/assessmentUtils";
import { ForwardArrowDeltaScreen } from "../ArchetypeDiscovery/ArchetypeDiscoveryScreen";
import ThemedButton from "@/components/shared/ThemedButton/ThemedButton";

type Props = {
  topTraitDeltaData?: {
    traitIdentifier: TraitIdentifier;
  };
  onFinish: () => void;
};

const font = Roboto_Mono({ subsets: ["latin"] });

export default function SingleTraitDeltaView(props: Props) {
  const elementsDiscovered = useSelector(
    (state: RootState) => state.user.elementsDiscovered
  );

  const opacity = useStaggeredOpacity(5);

  const discoveredElement = elementsDiscovered?.find(
    (e) => e.scaleSlug === props.topTraitDeltaData?.traitIdentifier.slug
  );
  const element = getElementFromTraitIdentifier(
    props.topTraitDeltaData?.traitIdentifier
  ) as ExtendedScale | null;

  const currrentScore = getUserScaleScore(
    discoveredElement?.scaleScore?.percentScore,
    props.topTraitDeltaData?.traitIdentifier.slug as string,
    discoveredElement?.scaleScore?.dimensionSlugSource
  );

  const displayedCurrentScore = currrentScore
    ? (currrentScore * 100).toFixed(0)
    : 0;

  const previousPercentScore = getPreviousPercentScoreFromScoreHistory(
    discoveredElement?.scaleScore?.scoreHistory
  );

  const previousScore = getUserScaleScore(
    previousPercentScore,
    props.topTraitDeltaData?.traitIdentifier.slug as string,
    discoveredElement?.scaleScore?.dimensionSlugSource
  );

  const displayedPreviousScore = previousScore
    ? (previousScore * 100).toFixed(0)
    : 0;

  return (
    <div className={styles.main}>
      <p style={{ opacity: opacity.step1 }} className={styles.topText}>
        GREATEST CHANGE
      </p>
      {element && (
        <div style={{ opacity: opacity.step2 }} className={styles.elementDiv}>
          <Element slug={element.slug} />
        </div>
      )}

      <div style={{ opacity: opacity.step3 }} className={styles.parent}>
        <div className={styles.inner}>
          <p className={styles.score}>
            {displayedPreviousScore}
            <span className={styles.span}>/100</span>
          </p>
          <p
            className={font.className}
            style={{ fontSize: "11px", fontWeight: "500", color: "#928E8C" }}
          >
            Previous score
          </p>
        </div>

        <ForwardArrowDeltaScreen />

        <div className={styles.inner}>
          <p className={styles.score}>
            {displayedCurrentScore}
            <span className={styles.span}>/100</span>
          </p>
          <p
            className={font.className}
            style={{ fontSize: "11px", fontWeight: "500", color: "#928E8C" }}
          >
            {"Today's score"}
          </p>
        </div>
      </div>

      <p style={{ opacity: opacity.step4 }} className={styles.bottom}>
        Your {element?.alias ?? element?.name} score changed the most since last
        time.
      </p>

      <div
        onClick={(e) => {
          e.stopPropagation();
          props.onFinish();
        }}
        style={{ opacity: opacity.step5 }}
        className={styles.bottomButton}
      >
        <ThemedButton
          style={{ fontWeight: "450", width: "100%" }}
          title="Continue to breakdown"
        />
      </div>
    </div>
  );
}
